// A WRITE KEEPS ITS KEY (V2, PR-E4 wave 6; spec 16 §9.29). Every saved-view
// write is sent with an Idempotency-Key, and the form that makes the write
// holds that key from its first send until the server has answered it: a
// success, or a refusal the server made (a name clash, a full cap, a 403). A
// failure with no answer from the server keeps the key. That is the browser
// refusing the write offline before it is sent, a request whose answer was
// lost on the way back, or the server's 409 `IDEMPOTENCY_IN_PROGRESS` while
// it still runs the first try. The retry then sends the same key, and the
// platform's idempotency store replays the first answer when the first try
// did land, so a lost answer never makes a second view.
//
// WHAT THE READER IS TOLD. Once a try under the held key has left the browser
// without an answer, every later failure of the same write says the change may
// have been saved, since the first try may have landed whatever happens to the
// next one. Before that, a write the browser refused offline says the reader
// is offline and nothing was saved, which is then true.
//
// One holder per write the reader opened: the Save as new form holds one, and
// each Manage row holds one per act (a rename, sharing, unsharing, a delete).
// Sharing and unsharing are two writes, because after a lost share the list
// is read again and may show the view shared. A press of Stop sharing then
// asks for something else, and must not be answered with the share's replay.

import { newIdempotencyKey } from "../../../../lib/request";
import { keepsIdempotencyKey, viewErrorOf, ViewsOfflineError, type ViewError } from "./view-errors";

export class WriteKey {
  private key: string | undefined = undefined;
  /** A try under the held key left the browser and got no answer. */
  private unanswered = false;

  /** The key to send: the one held, or a fresh one for a new write. */
  take(): string {
    this.key ??= newIdempotencyKey();
    return this.key;
  }

  /** The key held for a retry, if any. */
  get held(): string | undefined {
    return this.key;
  }

  /** The write settled. A success or a server's refusal releases the key; a
   * failure with no verdict keeps it. Returns what the reader is told. */
  settle(error?: unknown): ViewError | undefined {
    if (error === undefined) {
      this.release();
      return undefined;
    }
    if (!keepsIdempotencyKey(error)) {
      this.release();
      return viewErrorOf(error);
    }
    if (!(error instanceof ViewsOfflineError)) this.unanswered = true;
    const told = viewErrorOf(error);
    return this.unanswered ? { ...told, key: "views.errors.mayHaveSaved" } : told;
  }

  /** The reader abandoned the write (a cancelled rename or delete). */
  release(): void {
    this.key = undefined;
    this.unanswered = false;
  }
}

/** A holder per named write, for a surface that can make several (a Manage
 * row, the switcher's Save of each view). */
export class WriteKeys {
  private readonly held = new Map<string, WriteKey>();

  for(name: string): WriteKey {
    let key = this.held.get(name);
    if (key === undefined) {
      key = new WriteKey();
      this.held.set(name, key);
    }
    return key;
  }

  release(name: string): void {
    this.held.get(name)?.release();
  }
}
